import { Card, CardContent } from "@/components/ui/card";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  BookOpen,
  Brain,
  Building,
  Code,
  CreditCard,
  Database,
  FileText,
  Globe,
  Layers,
  MessageCircle,
  Settings,
  Shield,
  Smartphone,
  Users,
  Zap,
} from "lucide-react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

// FAQ categories data for 4x4 grid based on the comprehensive markdown documentation
const faqCategories = [
  {
    id: "getting-started",
    title: "Getting Started",
    description:
      "Learn the basics of Artifact and how it differs from other AI platforms",
    icon: <BookOpen className="w-8 h-8" />,
    color: "from-blue-500/20 to-violet-500/20",
    darkColor: "dark:from-blue-400/10 dark:to-violet-400/10",
    questionCount: 5,
  },
  {
    id: "using-artifact",
    title: "Using Artifact",
    description:
      "Master AI model switching, Thread Memory, and conversation features",
    icon: <MessageCircle className="w-8 h-8" />,
    color: "from-indigo-500/20 to-purple-500/20",
    darkColor: "dark:from-indigo-400/10 dark:to-purple-400/10",
    questionCount: 5,
  },
  {
    id: "documents-files",
    title: "Documents & Files",
    description:
      "Upload, process, and analyze files with Vector Database integration",
    icon: <FileText className="w-8 h-8" />,
    color: "from-cyan-500/20 to-blue-500/20",
    darkColor: "dark:from-cyan-400/10 dark:to-blue-400/10",
    questionCount: 5,
  },
  {
    id: "projects",
    title: "Projects",
    description:
      "Create collaborative workspaces with shared memory and team access",
    icon: <Layers className="w-8 h-8" />,
    color: "from-emerald-500/20 to-teal-500/20",
    darkColor: "dark:from-emerald-400/10 dark:to-teal-400/10",
    questionCount: 5,
  },
  {
    id: "tools",
    title: "Tools",
    description:
      "Deep Research, Web Search, code generation, and third-party integrations",
    icon: <Code className="w-8 h-8" />,
    color: "from-violet-500/20 to-purple-500/20",
    darkColor: "dark:from-violet-400/10 dark:to-purple-400/10",
    questionCount: 5,
  },
  {
    id: "subscription-plans",
    title: "Subscription Plans",
    description: "Understand pricing, usage limits, and billing options",
    icon: <CreditCard className="w-8 h-8" />,
    color: "from-amber-500/20 to-orange-500/20",
    darkColor: "dark:from-amber-400/10 dark:to-orange-400/10",
    questionCount: 5,
  },
  {
    id: "troubleshooting",
    title: "Troubleshooting",
    description:
      "Resolve issues with documents, collaboration, and AI responses",
    icon: <AlertTriangle className="w-8 h-8" />,
    color: "from-red-500/20 to-pink-500/20",
    darkColor: "dark:from-red-400/10 dark:to-pink-400/10",
    questionCount: 4,
  },
  {
    id: "account-management",
    title: "Account Management",
    description: "Manage billing, security, notifications, and data settings",
    icon: <Settings className="w-8 h-8" />,
    color: "from-purple-500/20 to-violet-500/20",
    darkColor: "dark:from-purple-400/10 dark:to-violet-400/10",
    questionCount: 5,
  },
  {
    id: "security-privacy",
    title: "Security & Privacy",
    description:
      "Data protection, encryption, compliance, and project isolation",
    icon: <Shield className="w-8 h-8" />,
    color: "from-green-500/20 to-emerald-500/20",
    darkColor: "dark:from-green-400/10 dark:to-emerald-400/10",
    questionCount: 5,
  },
  {
    id: "ai-model-comparisons",
    title: "AI Model Comparisons",
    description:
      "Choose the right model for coding, context, speed, and specific tasks",
    icon: <Brain className="w-8 h-8" />,
    color: "from-yellow-500/20 to-amber-500/20",
    darkColor: "dark:from-yellow-400/10 dark:to-amber-400/10",
    questionCount: 5,
  },
  {
    id: "integrations-api",
    title: "Integrations & API",
    description:
      "Connect with Slack, Google Drive, GitHub, and programmatic access",
    icon: <Globe className="w-8 h-8" />,
    color: "from-teal-500/20 to-cyan-500/20",
    darkColor: "dark:from-teal-400/10 dark:to-cyan-400/10",
    questionCount: 4,
  },
  {
    id: "data-management-export",
    title: "Data Management & Export",
    description: "Export projects, backup data, and manage retention policies",
    icon: <Database className="w-8 h-8" />,
    color: "from-sky-500/20 to-blue-500/20",
    darkColor: "dark:from-sky-400/10 dark:to-blue-400/10",
    questionCount: 5,
  },
  {
    id: "performance-limitations",
    title: "Performance & Limitations",
    description:
      "Rate limits, file sizes, concurrent sessions, and optimization",
    icon: <Zap className="w-8 h-8" />,
    color: "from-orange-500/20 to-red-500/20",
    darkColor: "dark:from-orange-400/10 dark:to-red-400/10",
    questionCount: 5,
  },
  {
    id: "use-cases-industries",
    title: "Use Cases & Industries",
    description:
      "Legal, development, research, and financial services applications",
    icon: <Building className="w-8 h-8" />,
    color: "from-rose-500/20 to-pink-500/20",
    darkColor: "dark:from-rose-400/10 dark:to-pink-400/10",
    questionCount: 4,
  },
  {
    id: "mobile-voice-features",
    title: "Mobile & Voice Features",
    description: "iOS app, Android plans, voice input, and cross-platform sync",
    icon: <Smartphone className="w-8 h-8" />,
    color: "from-lime-500/20 to-green-500/20",
    darkColor: "dark:from-lime-400/10 dark:to-green-400/10",
    questionCount: 5,
  },
  {
    id: "enterprise-team-features",
    title: "Enterprise & Team Features",
    description:
      "Permissions, collaborative editing, custom AI agents, and training materials",
    icon: <Users className="w-8 h-8" />,
    color: "from-gray-500/20 to-slate-500/20",
    darkColor: "dark:from-gray-400/10 dark:to-slate-400/10",
    questionCount: 4,
  },
];

export function FAQsPage() {
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const staggerItems = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
      },
    },
  };

  return (
    <>
      <Helmet>
        <title>Help Center & FAQs | Artifact - AI Assistant Support</title>
        <meta
          name="description"
          content="Find answers to your questions about Artifact AI assistant. Browse our comprehensive help center with guides for getting started, using AI models, file uploads, projects, and more."
        />

        {/* Keywords */}
        <meta
          name="keywords"
          content="Artifact help, AI assistant FAQ, ChatGPT help, Claude AI support, AI model comparison, file upload help, project collaboration, subscription plans, troubleshooting"
        />

        {/* Canonical URL */}
        <link rel="canonical" href="https://artifact.chat/docs" />

        {/* Open Graph Meta Tags */}
        <meta
          property="og:title"
          content="Help Center & FAQs | Artifact - AI Assistant Support"
        />
        <meta
          property="og:description"
          content="Find answers to your questions about Artifact AI assistant. Browse our comprehensive help center with guides for getting started, using AI models, file uploads, projects, and more."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://artifact.chat/docs" />
        <meta
          property="og:image"
          content="https://artifact.chat/assets/og-image.jpeg"
        />
        <meta property="og:site_name" content="Artifact" />

        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Help Center & FAQs | Artifact - AI Assistant Support"
        />
        <meta
          name="twitter:description"
          content="Find answers to your questions about Artifact AI assistant. Browse our comprehensive help center with guides for getting started, using AI models, file uploads, projects, and more."
        />
        <meta
          name="twitter:image"
          content="https://artifact.chat/assets/og-image.jpeg"
        />
        <meta name="twitter:site" content="@ArtifactAI" />

        {/* Additional Meta Tags */}
        <meta name="robots" content="index, follow" />

        {/* Structured Data for FAQ Page */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "How do I get started with Artifact?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Getting started with Artifact is easy. Simply sign up for a free account and begin chatting with multiple AI models including ChatGPT, Claude, and Gemini in one unified workspace.",
                },
              },
              {
                "@type": "Question",
                name: "What AI models does Artifact support?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Artifact supports 33+ AI models including ChatGPT, Claude, Gemini Pro, Groq, Mistral, Perplexity, and many more, all accessible from one platform.",
                },
              },
              {
                "@type": "Question",
                name: "How much does Artifact cost?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Artifact offers a free tier to get started. Pro plans start at $19.99/month with access to premium models and advanced features. Team plans are available for collaborative workspaces.",
                },
              },
            ],
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background relative">
        {/* Background Gradient */}
        <div className="fixed inset-0 bg-gradient-to-b from-background via-purple-950/10 dark:via-purple-950/20 to-background pointer-events-none z-0" />

        {/* Hero Section */}
        <section className="relative py-12 sm:py-20 overflow-hidden z-10">
          {/* Floating Gradient Orbs */}
          <motion.div
            className="absolute top-1/4 right-1/4 w-48 h-48 rounded-full bg-gradient-to-r from-purple-600/15 to-blue-500/15 blur-3xl"
            animate={{
              x: [0, 20, 0],
              y: [0, -20, 0],
              scale: [1, 1.05, 1],
            }}
            transition={{
              duration: 12,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
          <motion.div
            className="absolute bottom-1/4 left-1/4 w-32 h-32 rounded-full bg-gradient-to-r from-blue-600/10 to-purple-500/10 blur-2xl"
            animate={{
              x: [0, -15, 0],
              y: [0, 15, 0],
              scale: [1, 0.95, 1],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 2,
            }}
          />

          {/* Hero Content */}
          <div className="container relative z-10 mx-auto px-6">
            <motion.div
              className="text-center max-w-4xl mx-auto"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl md:text-6xl font-bold mb-6 pb-2 md:pb-4 bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-500 dark:from-purple-400 dark:to-blue-400">
                Help Center
              </h1>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Find answers to your questions and learn how to make the most of
                Artifact&apos;s AI-native workspace
              </p>
            </motion.div>
          </div>
        </section>

        {/* FAQ Categories Grid */}
        <section className="relative z-10 pb-24">
          <div className="container mx-auto px-6 max-w-7xl">
            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
              variants={staggerItems}
              initial="hidden"
              animate="visible"
            >
              {faqCategories.map((category, index) => (
                <motion.div
                  key={category.id}
                  variants={fadeIn}
                  whileHover={{ scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                >
                  <Link to={`/docs/${category.id}`} className="block h-full">
                    <Card className="h-full overflow-hidden rounded-2xl border border-gray-200/50 dark:border-gray-700/50 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm hover:shadow-lg dark:hover:shadow-2xl transition-all cursor-pointer group">
                      <CardContent className="p-6 text-center">
                        {/* Icon */}
                        <div
                          className={`mx-auto mb-4 w-14 h-14 rounded-2xl bg-gradient-to-br ${category.color} ${category.darkColor} flex items-center justify-center text-gray-700 dark:text-gray-300 group-hover:scale-110 transition-transform duration-300`}
                        >
                          {category.icon}
                        </div>

                        {/* Title */}
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                          {category.title}
                        </h3>

                        {/* Description */}
                        <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed mb-3">
                          {category.description}
                        </p>

                        {/* Question Count */}
                        <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                          {category.questionCount} questions
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Contact Support Section */}
        <section className="relative z-10 pb-16">
          <div className="container mx-auto px-6 max-w-4xl">
            <motion.div
              className="text-center bg-gradient-to-r from-purple-600/10 to-blue-600/10 dark:from-purple-400/10 dark:to-blue-400/10 rounded-2xl p-8 border border-purple-200/20 dark:border-purple-700/20 backdrop-blur-sm"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.5 }}
            >
              <h2 className="text-2xl font-bold mb-4 text-foreground">
                Still need help?
              </h2>
              <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
                Can&apos;t find what you&apos;re looking for? Our support team
                is here to help you get the most out of Artifact.
              </p>
              <Link
                to="/support"
                className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700 text-white font-medium rounded-full shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40 transition-all duration-300"
              >
                Contact Support
              </Link>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}

export default FAQsPage;
